/**
 * ObjectType 확장 스타일(kind/fontSize/labelWidth 등)의 API 동기화 헬퍼.
 *
 * 백엔드 object_types 테이블은 name/icon/color/priority/properties 만 영속한다.
 * kind(텍스트 라벨/선), 폰트·라벨 크기, stroke/fill 스타일 같은 확장 필드는
 * properties JSON 안에 예약 키('__style') 항목으로 직렬화해 왕복시킨다.
 * 이렇게 해야 사용자가 사이드바에서 추가한 타입이 에디터 재진입(loadTypes)
 * 후에도 동일한 설정으로 유지된다.
 */

import type { ObjectType, Property } from './types';

/** properties 배열 내 스타일 메타데이터 예약 키. UI 속성 편집기에는 노출하지 않는다. */
export const STYLE_PROPERTY_KEY = '__style';

/** API properties 항목 (백엔드는 default_value snake_case 를 사용). */
interface ApiProperty {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'array';
  required: boolean;
  default_value?: unknown;
  // 구버전/로컬 직렬화 호환
  defaultValue?: unknown;
}

/** __style 항목에 직렬화되는 확장 필드 모음. */
export interface ObjectTypeStyle {
  kind?: ObjectType['kind'];
  fontSize?: number;
  labelWidth?: number;
  labelHeight?: number;
  strokeColor?: string;
  fillColor?: string;
  opacity?: number;
  strokeWidth?: number;
  iconWidth?: number;
  iconHeight?: number;
  colorMode?: 'stroke' | 'fill';
}

const STYLE_FIELDS: readonly (keyof ObjectTypeStyle)[] = [
  'kind',
  'fontSize',
  'labelWidth',
  'labelHeight',
  'strokeColor',
  'fillColor',
  'opacity',
  'strokeWidth',
  'iconWidth',
  'iconHeight',
  'colorMode',
];

/** ObjectType(또는 동일 필드를 가진 폼 데이터)에서 스타일 필드만 추출. */
export function pickStyle(source: Partial<ObjectType>): ObjectTypeStyle {
  const style: ObjectTypeStyle = {};
  STYLE_FIELDS.forEach((field) => {
    const value = source[field as keyof ObjectType];
    if (value !== undefined) {
      (style as Record<string, unknown>)[field] = value;
    }
  });
  return style;
}

/**
 * API 전송용 properties 빌드: 사용자 정의 속성 + __style 항목.
 * 스타일 필드가 하나도 없으면 __style 을 생략한다.
 */
export function buildApiProperties(
  properties: { key: string; type: Property['type']; required: boolean }[],
  style: ObjectTypeStyle
): ApiProperty[] {
  const userProps: ApiProperty[] = properties
    .filter((p) => p.key !== STYLE_PROPERTY_KEY)
    .map((p) => ({ key: p.key, type: p.type, required: p.required }));

  if (Object.keys(style).length === 0) return userProps;

  return [
    ...userProps,
    {
      key: STYLE_PROPERTY_KEY,
      type: 'string',
      required: false,
      default_value: JSON.stringify(style),
    },
  ];
}

/**
 * API 응답 properties 에서 __style 을 분리해 스타일 필드와 표시용 속성으로 나눈다.
 * __style 이 없거나 파싱 실패하면 빈 스타일을 반환한다.
 */
export function extractStyleFromApiProperties(
  apiProperties: ApiProperty[] | undefined | null
): { style: ObjectTypeStyle; properties: Property[] } {
  const props = apiProperties || [];
  const styleEntry = props.find((p) => p.key === STYLE_PROPERTY_KEY);
  const rest = props
    .filter((p) => p.key !== STYLE_PROPERTY_KEY)
    .map((p) => ({
      key: p.key,
      type: p.type,
      required: p.required,
      defaultValue: p.default_value ?? p.defaultValue,
    })) as Property[];

  if (!styleEntry) return { style: {}, properties: rest };

  const raw = styleEntry.default_value ?? styleEntry.defaultValue;
  if (typeof raw !== 'string') return { style: {}, properties: rest };

  try {
    const parsed = JSON.parse(raw) as ObjectTypeStyle;
    return { style: pickStyle(parsed as Partial<ObjectType>), properties: rest };
  } catch {
    return { style: {}, properties: rest };
  }
}
